import { createRouter, createWebHistory } from "vue-router";

import { useTariffStore } from "@/entities";
import { ROUTES, isAuthRoute, hasToken } from "@/pages";
import { AUTHENTICATION_ROUTE_NAMES, GENERAL_ROUTE_NAMES } from "@/shared";

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: ROUTES,
});

router.beforeEach((to) => {
    // Публичные страницы (статус заказа для клиента) — без авторизации и тарифных гейтов.
    if (to.meta?.public) return;

    const isAuthenticated = hasToken();
    const isCurrentRouteAuth = isAuthRoute(to.name);
    const tariffStore = useTariffStore();

    if (
        tariffStore.statistic?.daysLeft === 0 &&
        to.name !== GENERAL_ROUTE_NAMES.BUY_TARIFF
    ) {
        return { name: GENERAL_ROUTE_NAMES.BUY_TARIFF };
    }

    if (!isAuthenticated && !isCurrentRouteAuth) {
        return { name: AUTHENTICATION_ROUTE_NAMES.LOGIN };
    }

    if (isAuthenticated && isCurrentRouteAuth) {
        return { name: GENERAL_ROUTE_NAMES.DEFAULT };
    }

    if (to.query.token) {
        return { name: to.name, query: {} };
    }
});

// После деплоя Vite пересобирает чанки страниц с новыми хэшами и удаляет старые
// с сервера. Вкладка, открытая до деплоя, при переходе в раздел грузит старый
// чанк (`component: () => import(...)`): сервер вместо .js отдаёт index.html,
// динамический импорт падает — и клик по пункту меню молча «не срабатывает»,
// пока страницу не перезагрузят вручную. Ловим такую ошибку и один раз жёстко
// открываем нужный маршрут: свежий index.html подтягивает актуальные хэши.
const CHUNK_RELOAD_KEY = "router:reloaded-on-chunk-error";

const isChunkLoadError = (error) => {
    const message = error?.message ?? "";

    return (
        message.includes("Failed to fetch dynamically imported module") ||
        message.includes("error loading dynamically imported module") ||
        message.includes("Importing a module script failed")
    );
};

const reloadOnceForChunkError = (targetPath) => {
    let alreadyReloaded = false;

    try {
        alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY) === "1";
        sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
    } catch {
        // sessionStorage недоступен (приватный режим) — навигации не мешаем.
    }

    // Повторная та же ошибка сразу после перезагрузки означает, что она не
    // помогла (чанка действительно нет) — не зацикливаемся.
    if (!alreadyReloaded) {
        const path = targetPath || window.location.href;
        // index.html отдаётся без Cache-Control и кэшируется браузером, поэтому обычный
        // reload тянет старый index со ссылками на удалённые чанки. Уникальный query форсит
        // свежий запрос index.html с актуальными хэшами.
        const separator = path.includes("?") ? "&" : "?";

        window.location.assign(`${path}${separator}_cb=${Date.now()}`);
    }
};

router.onError((error, to) => {
    if (isChunkLoadError(error)) {
        reloadOnceForChunkError(to?.fullPath);
    }
});

// Флаг снимаем только после успешной навигации (в том числе после жёсткой
// перезагрузки на нужный маршрут), иначе потеряем защиту от зацикливания.
router.afterEach(() => {
    try {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    } catch {
        // sessionStorage недоступен — игнорируем.
    }
});

// Подстраховка для чанков, которые грузятся вне навигации роутера (например,
// асинхронные компоненты внутри страницы): Vite шлёт это событие, когда preload
// динамического модуля не удался. router.onError отрабатывает раньше и знает
// целевой маршрут, поэтому здесь только откладываем перезагрузку текущего.
window.addEventListener("vite:preloadError", () => {
    setTimeout(() => reloadOnceForChunkError(), 0);
});

export const withRouter = (application) => {
    application.use(router);
};

export { router };
